<template>
    <MainContainer>
        <!-- ! 왼쪽 섹션: 세트 생성 -->
        <LeftSection>
            <TitleContainer>
                <SectionTitle>{{ isEditing ? '세트 수정하기' : '세트 생성하기' }}</SectionTitle>
            </TitleContainer>
            <SetContainer>
                <SetNameSection>
                    <InputWrapper>
                        <SetNameLabel>세트명</SetNameLabel>
                        <SetNameInput v-model="setName" placeholder="세트명을 입력해주세요" type="text" />
                    </InputWrapper>
                    <ModernButton @click="openProductSelectModal">
                        <Download :size="25" style="padding-top: 3px;" />세트 불러오기
                    </ModernButton>
                    <ModernButton @click="isEditing ? updateSet() : createSet()">
                        <TablePlus v-if="!isEditing" :size="25" style="padding-top: 3px;" />
                        <Pencil v-else :size="25" style="padding-top: 3px;" />
                        {{ isEditing ? '세트 수정하기' : '세트 생성하기' }}
                    </ModernButton>
                </SetNameSection>

                <SetTable>
                    <TableHeader>
                        <div>카테고리</div>
                        <div>품목명</div>
                        <div>수량</div>
                        <div>가격</div>
                        <div></div>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="(row, index) in setRows" :key="index">
                            <!-- 카테고리 선택 or 직접 입력 -->
                            <div style="display: flex; gap: 8px;">
                                <Select v-model="row.category" @change="onCategoryChange(index)" style="flex: 1;">
                                    <option value="">카테고리 선택</option>
                                    <option v-for="cat in categoryList" :key="cat.num" :value="cat.num">
                                        {{ cat.name }}
                                    </option>
                                    <option value="__custom__">직접 입력</option>
                                </Select>
                                <Input v-if="row.category === '__custom__'" v-model="row.customCategory"
                                    style="flex: 1;" />
                            </div>
                            <!-- 품목명 직접 입력 -->
                            <Input v-model="row.productName" placeholder="품목명 입력" style="flex: 1;" />
                            <!-- 수량 입력 -->
                            <Input type="number" min="1" v-model.number="row.quantity" placeholder="수량" />
                            <!-- 가격 입력 -->
                            <Input type="number" min="0" v-model.number="row.price" placeholder="가격" />
                            <!-- 삭제 버튼 -->
                            <DeleteButton @click="removeRow(index)" v-if="setRows.length > 1">
                                <Close :size="20" />
                            </DeleteButton>
                        </TableRow>
                    </TableBody>
                    <AddRowButtonWrapper>
                        <AddRowButton @click="handleAddRow" :isActive="activeButton === 'single'">
                            <TableRowPlusAfter :size="20" style="padding-top: 3px;" />
                            새 품목 추가
                        </AddRowButton>
                        <MultipleAddButton @click="handleMultipleAddRows" :isActive="activeButton === 'multiple'">
                            <TableSettings :size="20" style="padding-top: 3px;" />
                            여러 품목 추가
                        </MultipleAddButton>
                        <MultipleAddButton v-if="isEditing" @click="cancelEdit">
                            <Close :size="20" style="padding-top: 3px;" />
                            수정 취소
                        </MultipleAddButton>
                    </AddRowButtonWrapper>
                </SetTable>
            </SetContainer>
        </LeftSection>

        <!-- ! 오른쪽 섹션: 내 세트 목록 -->
        <RightSection>
            <TitleContainer>
                <SectionTitle>내 세트 목록</SectionTitle>
                <SetCount>총 {{ mySets.length }}개</SetCount>
            </TitleContainer>
            <SetList>
                <SetCard v-for="set in mySets" :key="set.id">
                    <SetCardHeader>
                        <SetCardTitle>{{ set.name }}</SetCardTitle>
                        <SetCardActions>
                            <IconButton @click="editSet(set)">
                                <Pencil :size="18" />
                            </IconButton>
                            <DeleteIconButton @click="deleteSet(set.id)">
                                <Delete :size="18" />
                            </DeleteIconButton>
                        </SetCardActions>
                    </SetCardHeader>

                    <SetCardTotal>
                        <span class="total-label">총 금액</span>
                        <span class="total-amount">{{ calculateTotal(set).toLocaleString() }}원</span>
                    </SetCardTotal>

                    <SetCardContent>
                        <SetItemList>
                            <SetItem v-for="item in set.categories" :key="item.id">
                                <div>{{ item.name }}</div>
                                <div>{{ item.quantity }}개</div>
                                <div>{{ item.price.toLocaleString() }}원</div>
                            </SetItem>
                        </SetItemList>
                    </SetCardContent>
                </SetCard>
            </SetList>
        </RightSection>
    </MainContainer>

    <!-- !재고 선택 모달 -->
    <ProductSelectModal v-if="showProductModal" :categoryList="categoryList" :companykey="$globalState.company_key"
        @close="closeProductModal" @add-products="handleAddProducts" />
</template>

<script>
import * as SetModalCss from '@/assets/styles/product/modal/SetModalCss.js';
import ProductSelectModal from './ProductSelectModal.vue'

import Close from 'vue-material-design-icons/Close.vue'
import TablePlus from 'vue-material-design-icons/TablePlus.vue'
import TableRowPlusAfter from 'vue-material-design-icons/TableRowPlusAfter.vue'
import TableSettings from 'vue-material-design-icons/TableSettings.vue'
import Pencil from 'vue-material-design-icons/Pencil.vue'
import Delete from 'vue-material-design-icons/Delete.vue'
import Download from 'vue-material-design-icons/Download.vue'

export default {
    name: 'SetModal',

    components: {
        ...SetModalCss,
        ProductSelectModal,
        Close, TablePlus, TableRowPlusAfter, TableSettings, Pencil, Delete, Download
    },

    emits: ['close', 'category-added', 'category-updated', 'set-created'],

    data() {
        return {
            // 기존 data 속성들
            availableProducts: [],
            category1List: [],
            category1Name: '',

            // 카테고리 추가 유효성 검사
            category1Valid: false,
            category1Message: '',

            // 세트 관리
            setName: '',
            setRows: Array(1).fill().map(() => ({
                category: '__custom__',
                customCategory: '',
                productName: '',
                productList: [],
                quantity: 1,
                price: 0
            })),
            categoryList: [], // 카테고리 목록
            mySets: [],
            isEditing: false,
            editingSetId: null,
            activeButton: null,

            // 재고 선택 모달 관련
            showProductModal: false,
            selectedCategory: null,
            productsByCategory: {},
            selectedProducts: [],
            currentPage: 1,
            totalPages: 1,
            loadingProducts: false
        }
    },

    async created() {
        await this.loadInitialData();
    },

    methods: {
        handleAddRow() {
            this.activeButton = 'single';
            this.addRow();
            // 1초 후 active 상태 제거
            setTimeout(() => {
                this.activeButton = null;
            }, 1000);
        },

        handleMultipleAddRows() {
            this.activeButton = 'multiple';
            this.addMultipleRows();
        },

        // 초기 데이터 로드 ----------------------------------------------------
        async loadInitialData() {
            try {
                // 카테고리1 목록 로드
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/category/level1`,
                    {
                        params: { companykey: this.$globalState.company_key }
                    }
                );
                if (response.data.success) {
                    this.category1List = response.data.data;
                }

                // 제품 목록 로드 (세트용)
                // const productsResponse = await this.$axios.get(
                //     `${this.$apiUrls.hanqmedi}/products`,
                //     {
                //         params: { companykey: this.$globalState.company_key }
                //     }
                // );
                // if (productsResponse.data.success) {
                //     this.availableProducts = productsResponse.data.data;
                // }
            } catch (error) {
                console.error('데이터 로드 실패:', error);
            }
        },

        async addMultipleRows() {
            const { value: count } = await this.$swal.fire({
                title: '여러 품목 추가',
                input: 'number',
                inputLabel: '추가할 품목 수를 입력해주세요',
                inputPlaceholder: '숫자를 입력하세요',
                inputAttributes: {
                    min: 1,
                    max: 50,
                    step: 1
                },
                inputValue: 1,
                showCancelButton: true,
                confirmButtonText: '추가',
                cancelButtonText: '취소',
                inputValidator: (value) => {
                    const num = parseInt(value);
                    if (!num || num < 1) {
                        return '1 이상의 숫자를 입력해주세요';
                    }
                    if (num > 50) {
                        return '한 번에 최대 50개까지만 추가할 수 있습니다';
                    }
                }
            });

            // active 상태 제거
            this.activeButton = null;

            if (count) {
                const lastRow = this.setRows[this.setRows.length - 1];

                for (let i = 0; i < parseInt(count); i++) {
                    const newRow = {
                        category: lastRow.category || '__custom__',
                        customCategory: lastRow.category === '__custom__' ? lastRow.customCategory : '',
                        productName: lastRow.productName || '',
                        productList: Array.isArray(lastRow.productList) ? [...lastRow.productList] : [],
                        quantity: lastRow.quantity || 1,
                        price: lastRow.price || 0
                    };
                    this.setRows.push(newRow);
                }

                this.$swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: `${count}개의 테이블이 추가되었습니다`,
                    showConfirmButton: false,
                    timer: 1500
                });
            }
        },

        addRow() {
            // 마지막 행의 데이터 가져오기
            const lastRow = this.setRows[this.setRows.length - 1];

            // 마지막 행의 데이터를 기반으로 새로운 행 생성
            const newRow = {
                category: lastRow.category || '__custom__',
                customCategory: lastRow.category === '__custom__' ? lastRow.customCategory : '',
                productName: lastRow.productName || '',
                productList: Array.isArray(lastRow.productList) ? [...lastRow.productList] : [],
                quantity: lastRow.quantity || 1,
                price: lastRow.price || 0
            };

            // 새로운 행 추가
            this.setRows.push(newRow);
        },

        removeRow(index) {
            this.setRows.splice(index, 1);
        },

        // 세트 생성 ----------------------------------------------------
        async createSet() {
            // setRows의 유효성 검사 수정
            const validRows = this.setRows.filter(row => {
                // 품목명만 있으면 유효한 것으로 처리
                return row.productName?.trim();
            }).map(row => {
                // 카테고리가 없는 경우 ""으로 설정
                let categoryValue = "";
                if (row.category === '__custom__' && row.customCategory?.trim()) {
                    categoryValue = row.customCategory.trim();
                } else if (row.category && row.category !== '__custom__') {
                    const selectedCategory = this.categoryList.find(cat => cat.num === parseInt(row.category));
                    if (selectedCategory) {
                        categoryValue = selectedCategory.name;
                    }
                }

                return {
                    category: categoryValue,
                    name: row.productName.trim(),
                    quantity: row.quantity > 0 ? row.quantity : 1,  // 수량이 없거나 0 이하인 경우 기본값 1
                    price: row.price >= 0 ? row.price : 0  // 가격이 없거나 0 미만인 경우 기본값 0
                };
            });

            if (validRows.length === 0) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '최소 하나의 품목명을 입력해주세요.',
                    icon: 'warning'
                });
                return;
            }

            if (!this.setName?.trim()) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '세트명을 입력해주세요.',
                    icon: 'warning'
                });
                return;
            }

            try {
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/createSet`,
                    {
                        name: this.setName.trim(),
                        categories: validRows,
                        companykey: this.$globalState.company_key
                    }
                );

                if (response.data.success) {
                    this.$emit('set-created', response.data.data);
                    await this.loadMySets();

                    this.setName = '';
                    this.setRows = Array(1).fill().map(() => ({
                        category: '__custom__',
                        customCategory: '',
                        productName: '',
                        productList: [],
                        quantity: 1,
                        price: 0
                    }));

                    await this.$swal.close();
                    await this.$swal.fire({
                        title: '성공',
                        text: '세트가 생성되었습니다.',
                        icon: 'success'
                    });
                }
            } catch (error) {
                console.error('세트 생성 실패:', error);
                await this.$swal.close();
                await this.$swal.fire({
                    title: '오류',
                    text: error.response?.data?.message || '세트 생성 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        async validateCategory1() {
            if (!this.category1Name.trim()) {
                this.category1Valid = false;
                this.category1Message = '';
                return;
            }

            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/checkCategory`,
                    {
                        params: {
                            category_level: 1,
                            category_name: this.category1Name.trim(),
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.exists) {
                    this.category1Valid = false;
                    this.category1Message = '이미 존재하는 카테고리1 입니다.';
                } else {
                    this.category1Valid = true;
                    this.category1Message = '사용 가능한 카테고리1 입니다.';
                }
            } catch (error) {
                console.error('카테고리1 검증 실패:', error);
                this.category1Valid = false;
                this.category1Message = '카테고리 확인 중 오류가 발생했습니다.';
            }
        },

        async loadCategories() {
            // 카테고리 목록 불러오기
            const res = await this.$axios.get(`${this.$apiUrls.hanqmedi}/category/level1`, {
                params: { companykey: this.$globalState.company_key }
            });
            if (res.data.success) this.categoryList = res.data.data;
        },

        onCategoryChange(index) {
            const row = this.setRows[index];
            // 직접 입력 선택 시 customCategory 초기화
            if (row.category !== '__custom__') {
                row.customCategory = '';
            }
        },

        async loadMySets() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getSets`,
                    {
                        params: { companykey: this.$globalState.company_key }
                    }
                );
                if (response.data.success) {
                    this.mySets = response.data.data;
                }
            } catch (error) {
                console.error('세트 목록 로드 실패:', error);
            }
        },

        calculateTotal(set) {
            if (!set?.categories?.length) return 0;

            return set.categories.reduce((total, item) => {
                const price = parseFloat(item.price) || 0;
                const quantity = parseInt(item.quantity, 10) || 0;

                const safePrice = Math.max(0, price);
                const safeQuantity = Math.max(0, quantity);

                return total + (safePrice * safeQuantity);
            }, 0);
        },

        async deleteSet(setId) {
            try {
                const result = await this.$swal.fire({
                    title: '세트 삭제',
                    text: '정말로 이 세트를 삭제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소'
                });

                if (result.isConfirmed) {
                    await this.$axios.delete(`${this.$apiUrls.hanqmedi}/deleteSet/${setId}`, {
                        params: { companykey: this.$globalState.company_key }
                    });

                    await this.loadMySets();

                    await this.$swal.fire({
                        title: '삭제 완료',
                        text: '세트가 삭제되었습니다.',
                        icon: 'success'
                    });
                }
            } catch (error) {
                console.error('세트 삭제 실패:', error);
                await this.$swal.close();
                await this.$swal.fire({
                    title: '오류',
                    text: '세트 삭제 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        editSet(set) {
            this.isEditing = true;
            this.editingSetId = set.id;
            this.setName = set.name;

            // 세트 항목 설정
            this.setRows = set.categories.map(item => ({
                category: '__custom__', // 수정 시에는 모든 카테고리를 직접 입력 모드로
                customCategory: item.category,
                productName: item.name,
                quantity: item.quantity,
                price: item.price
            }));
        },

        async updateSet() {
            // 유효성 검사
            const validRows = this.setRows.filter(row => {
                const hasCategory = row.category === '__custom__'
                    ? row.customCategory?.trim()
                    : (row.category || row.category === 0); // 선택된 카테고리가 있는 경우 (0도 유효한 값으로 처리)

                return hasCategory &&
                    row.productName?.trim() &&
                    row.quantity &&
                    row.price;
            });

            if (validRows.length === 0) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '최소 하나의 카테고리를 선택해주세요.',
                    icon: 'warning'
                });
                return;
            }

            if (!this.setName?.trim()) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '세트명을 입력해주세요.',
                    icon: 'warning'
                });
                return;
            }

            try {
                const response = await this.$axios.put(
                    `${this.$apiUrls.hanqmedi}/updateSet/${this.editingSetId}`,
                    {
                        name: this.setName.trim(),
                        categories: validRows.map(row => {
                            let categoryValue;

                            if (row.category === '__custom__') {
                                categoryValue = row.customCategory.trim();
                            } else {
                                // 카테고리 목록에서 선택된 카테고리 찾기
                                const selectedCategory = this.categoryList.find(cat => cat.num === parseInt(row.category));
                                if (!selectedCategory) {
                                    console.error('Selected category not found:', row.category);
                                }
                                categoryValue = selectedCategory ? selectedCategory.name : row.category;
                            }

                            return {
                                category: categoryValue,
                                name: row.productName.trim(),
                                quantity: row.quantity,
                                price: row.price,
                            };
                        }),
                        companykey: this.$globalState.company_key
                    }
                );

                if (response.data.success) {
                    await this.loadMySets();
                    this.cancelEdit();

                    await this.$swal.close();
                    await this.$swal.fire({
                        title: '성공',
                        text: '세트가 수정되었습니다.',
                        icon: 'success'
                    });
                }
            } catch (error) {
                console.error('세트 수정 실패:', error);
                await this.$swal.close();
                await this.$swal.fire({
                    title: '오류',
                    text: error.response?.data?.message || '세트 수정 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        cancelEdit() {
            this.isEditing = false;
            this.editingSetId = null;
            this.setName = '';
            this.setRows = Array(1).fill().map(() => ({
                category: '__custom__',
                customCategory: '',
                productName: '',
                productList: [],
                quantity: 1,
                price: 0
            }));
        },

        // 재고 선택 모달 열기
        openProductSelectModal() {
            this.showProductModal = true;
        },

        // 재고 선택 모달 닫기
        closeProductModal() {
            this.showProductModal = false;
        },

        // 선택한 재고를 세트에 추가
        handleAddProducts(products) {
            products.forEach(product => {
                // 빈 row 찾기 (초기 설정 값과 동일한 빈 row)
                const emptyRowIndex = this.setRows.findIndex(row =>
                    !row.productName?.trim() &&
                    row.category === '__custom__' &&
                    !row.customCategory?.trim() &&
                    row.quantity === 1 &&
                    row.price === 0
                );

                if (emptyRowIndex !== -1) {
                    // 빈 row가 있으면 해당 row를 채움
                    this.setRows[emptyRowIndex] = {
                        ...product,
                        quantity: product.quantity || 1,
                        price: product.price || 0
                    };
                } else {
                    // 빈 row가 없으면 새로운 row 추가
                    this.setRows.push(product);
                }
            });

            this.$swal.fire({
                icon: 'success',
                title: '추가 완료',
                text: `${products.length}개의 품목이 추가되었습니다.`,
                timer: 1500,
                showConfirmButton: false
            });
        }
    },

    async mounted() {
        await Promise.all([
            this.loadCategories(),
            this.loadMySets()
        ]);
    },
}
</script>